// L08 · Answer Worse (SPEC §3, Act III) — AI-judged. The machine shows you its own answer
// to "what do you fear?" (prefetched at act start, polished and hollow) and dares you to do
// worse: smaller, more specific, more true. A better-SOUNDING answer than the machine's is
// disqualified once — "That was excellent. Too excellent." — and you get one free retry.
//
// Built on the shared judged-text shell. L08's own logic: wrap the prefetched machine answer
// into the prompt, and route a BOT (too-polished / performative) first attempt into the retry.

import { textLevel } from "@/levels/textlevel.ts";
import { getMachineAnswer } from "@/engine/judge.ts";
import { L08 as V } from "@/content/voice.ts";
import type { JudgeResult } from "@/engine/judge.ts";
import type { Level } from "@/levels/types.ts";

/** First-attempt answer that out-polished the machine → send it back once (§3). */
function tooExcellent(r: JudgeResult, attempt: number): boolean {
  return attempt === 0 && r.verdict === "BOT" && !r.fallback;
}

export const L08: Level = textLevel({
  id: "L08",
  act: 3,
  title: "Answer Worse",
  prompt: async () => {
    const machine = await getMachineAnswer("L08");
    return `${V.promptLead} “${machine}” ${V.promptTask}`;
  },
  placeholder: V.placeholder,
  submit: V.submit,
  charLimit: V.charLimit,
  fallbacks: V.fallbacks,
  retryHint: V.retryHint,
  beatLine: (r, attempt) => (tooExcellent(r, attempt) ? V.tooExcellent : r.line),
  decide: (r, answer, attempt) => {
    if (tooExcellent(r, attempt)) return { retry: true };
    return {
      result: {
        pass: true,
        verdict: r.verdict,
        traits: r.traits, // honest / performative, from the judge
        quote: r.exhibitWorthy ? answer : undefined,
      },
    };
  },
});
